// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Notification, NotificationCode, SendNotificationRequest } from '../../types/notifications';
import { UserRole } from '../../types/auth';
import { consoleService } from '../../services/consoleService';
import { authService } from '../../services/authService';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../ConfirmDialog';
import './NotificationDetail.scss';

// SVG Icons
const RedTriangleIcon = () => (
  <img src="/static/svg/red-triangle.svg" alt="" width="16" height="16" className="mr-2" />
);

const LoadingSpinner = () => (
  <div className="spinner-border spinner-border-sm" role="status">
    <span className="visually-hidden">Loading...</span>
  </div>
);

const NotificationDetail: React.FC = () => {
  const { notificationId } = useParams<{ notificationId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { dialogState, openConfirmDialog, closeConfirmDialog, handleConfirm } = useConfirmDialog();

  const [notification, setNotification] = useState<Notification | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showSendForm, setShowSendForm] = useState(false);
  const [sendForm, setSendForm] = useState<SendNotificationRequest>({
    user_id: '',
    subject: '',
    content: '',
    code: NotificationCode.CUSTOM,
    sender_id: '',
    persistent: true,
  });

  const sessionRole = authService.getSessionRole();
  const userId = searchParams.get('user_id') || '';

  useEffect(() => {
    if (notificationId && userId) {
      loadNotification();
    }
  }, [notificationId, userId]);

  const loadNotification = useCallback(async () => {
    if (!notificationId || !userId) return;

    setLoading(true);
    setError('');

    try {
      const result = await consoleService.getNotification('', notificationId, userId);
      setNotification(result);
      
      // Pre-populate send form with current notification data
      setSendForm({
        user_id: result.user_id,
        subject: result.subject,
        content: result.content,
        code: result.code,
        sender_id: result.sender_id || '',
        persistent: result.persistent,
      });
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load notification');
    } finally {
      setLoading(false);
    }
  }, [notificationId, userId]);

  const handleBackToNotifications = () => {
    navigate('/notifications');
  };

  const handleDeleteNotification = () => {
    if (!notification) return;

    openConfirmDialog(
      'Delete Notification',
      `Are you sure you want to delete this notification? This action cannot be undone.`,
      async () => {
        try {
          await consoleService.deleteNotification('', {
            notification_id: notification.id,
            user_id: notification.user_id
          });
          navigate('/notifications');
        } catch (err: any) {
          setError(err.response?.data?.message || err.message || 'Failed to delete notification');
        }
      }
    );
  };

  const handleSendNotification = async () => {
    if (!sendForm.user_id.trim() || !sendForm.subject.trim() || !sendForm.content.trim()) {
      setError('User ID, subject, and content are required');
      return;
    }

    setSending(true);
    setError('');

    try {
      await consoleService.sendNotification('', sendForm);
      setShowSendForm(false);
      setError('');
      // Show success message
      setError('Notification sent successfully!');
      setTimeout(() => setError(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const handleSendFormChange = (field: keyof SendNotificationRequest, value: any) => {
    setSendForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const sendAllowed = () => {
    return sessionRole === UserRole.USER_ROLE_ADMIN;
  };

  const deleteAllowed = () => {
    return sessionRole === UserRole.USER_ROLE_ADMIN;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
  };

  const getNotificationTypeText = (code: number) => {
    switch (code) {
      case NotificationCode.FRIEND_REQUEST: return 'Friend Request';
      case NotificationCode.FRIEND_ACCEPT: return 'Friend Accept';
      case NotificationCode.FRIEND_REJECT: return 'Friend Reject';
      case NotificationCode.FRIEND_REMOVE: return 'Friend Remove';
      case NotificationCode.GROUP_ADD: return 'Group Add';
      case NotificationCode.GROUP_JOIN_REQUEST: return 'Group Join Request';
      case NotificationCode.GROUP_JOIN_ACCEPT: return 'Group Join Accept';
      case NotificationCode.GROUP_JOIN_REJECT: return 'Group Join Reject';
      case NotificationCode.GROUP_KICK: return 'Group Kick';
      case NotificationCode.GROUP_PROMOTE: return 'Group Promote';
      case NotificationCode.GROUP_DEMOTE: return 'Group Demote';
      case NotificationCode.GROUP_BAN: return 'Group Ban';
      case NotificationCode.CHAT_MESSAGE: return 'Chat Message';
      case NotificationCode.CHAT_CHANNEL_UPDATE: return 'Chat Channel Update';
      case NotificationCode.LEADERBOARD_RECORD: return 'Leaderboard Record';
      case NotificationCode.TOURNAMENT_START: return 'Tournament Start';
      case NotificationCode.TOURNAMENT_END: return 'Tournament End';
      case NotificationCode.CUSTOM: return 'Custom';
      default: return `Code ${code}`;
    }
  };

  const getNotificationTypeBadgeClass = (code: number) => {
    switch (code) {
      case NotificationCode.FRIEND_REQUEST:
      case NotificationCode.FRIEND_ACCEPT:
      case NotificationCode.FRIEND_REJECT:
      case NotificationCode.FRIEND_REMOVE:
        return 'friend';
      case NotificationCode.GROUP_ADD:
      case NotificationCode.GROUP_JOIN_REQUEST:
      case NotificationCode.GROUP_JOIN_ACCEPT:
      case NotificationCode.GROUP_JOIN_REJECT:
      case NotificationCode.GROUP_KICK:
      case NotificationCode.GROUP_PROMOTE:
      case NotificationCode.GROUP_DEMOTE:
      case NotificationCode.GROUP_BAN:
        return 'group';
      case NotificationCode.CHAT_MESSAGE:
      case NotificationCode.CHAT_CHANNEL_UPDATE:
        return 'chat';
      case NotificationCode.LEADERBOARD_RECORD:
        return 'leaderboard';
      case NotificationCode.TOURNAMENT_START:
      case NotificationCode.TOURNAMENT_END:
        return 'tournament';
      case NotificationCode.CUSTOM:
        return 'custom';
      default:
        return 'unknown';
    }
  };

  if (loading) {
    return (
      <div className="notification-detail-container">
        <div className="alert alert-info">
          <LoadingSpinner />
          <strong className="ms-2">Loading notification...</strong>
        </div>
      </div>
    );
  }

  if (!notification) {
    return (
      <div className="notification-detail-container">
        <div className="alert alert-danger">
          <RedTriangleIcon />
          <strong>Notification not found</strong>
        </div>
      </div>
    );
  }

  return (
    <div className="notification-detail-container">
      <div className="notification-detail-header">
        <div className="header-actions">
          <button
            type="button"
            className="btn btn-outline-secondary back-btn"
            onClick={handleBackToNotifications}
          >
            ← Back to Notifications
          </button>
        </div>
        <h2 className="notification-title">Notification Details</h2>
        <h6 className="notification-subtitle">ID: {notification.id}</h6>
      </div>

      <div className="notification-info-section">
        <div className="info-grid">
          <div className="info-item">
            <label>Type:</label>
            <span className={`type-badge ${getNotificationTypeBadgeClass(notification.code)}`}>
              {getNotificationTypeText(notification.code)}
            </span>
          </div>
          <div className="info-item">
            <label>Code:</label>
            <span className="code-value">{notification.code}</span>
          </div>
          <div className="info-item">
            <label>Persistent:</label>
            <span className={`persistent-badge ${notification.persistent ? 'persistent' : 'temporary'}`}>
              {notification.persistent ? 'Yes' : 'No'}
            </span>
          </div>
          <div className="info-item">
            <label>Recipient:</label>
            <div className="recipient-info">
              <div className="username">{notification.username || '-'}</div>
              <code className="user-id">{notification.user_id}</code>
            </div>
          </div>
          <div className="info-item">
            <label>Sender ID:</label>
            <code className="sender-id">{notification.sender_id || '-'}</code>
          </div>
          <div className="info-item">
            <label>Created:</label>
            <span>{formatDate(notification.create_time)}</span>
          </div>
        </div>
      </div>

      <div className="notification-content-section">
        <h4>Subject</h4>
        <div className="subject-content">
          {notification.subject}
        </div>

        <h4>Content</h4>
        <div className="message-content">
          <pre className="content-text">{notification.content}</pre>
        </div>
      </div>

      {sendAllowed() && (
        <div className="send-notification-section">
          <div className="send-header">
            <h4>Send Similar Notification</h4>
            <button
              type="button"
              className="btn btn-outline-primary toggle-btn"
              onClick={() => setShowSendForm(!showSendForm)}
            >
              {showSendForm ? 'Hide Form' : 'Show Form'}
            </button>
          </div>

          {showSendForm && (
            <div className="send-form">
              <div className="form-grid">
                <div className="form-group">
                  <label>User ID *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={sendForm.user_id}
                    onChange={(e) => handleSendFormChange('user_id', e.target.value)}
                    placeholder="Enter user ID"
                  />
                </div>
                <div className="form-group">
                  <label>Subject *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={sendForm.subject}
                    onChange={(e) => handleSendFormChange('subject', e.target.value)}
                    placeholder="Enter subject"
                  />
                </div>
                <div className="form-group">
                  <label>Code</label>
                  <select
                    className="form-control"
                    value={sendForm.code}
                    onChange={(e) => handleSendFormChange('code', parseInt(e.target.value))}
                  >
                    <option value={NotificationCode.CUSTOM}>Custom</option>
                    <option value={NotificationCode.FRIEND_REQUEST}>Friend Request</option>
                    <option value={NotificationCode.GROUP_ADD}>Group Add</option>
                    <option value={NotificationCode.CHAT_MESSAGE}>Chat Message</option>
                    <option value={NotificationCode.LEADERBOARD_RECORD}>Leaderboard Record</option>
                    <option value={NotificationCode.TOURNAMENT_START}>Tournament Start</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Sender ID</label>
                  <input
                    type="text"
                    className="form-control"
                    value={sendForm.sender_id}
                    onChange={(e) => handleSendFormChange('sender_id', e.target.value)}
                    placeholder="Enter sender ID (optional)"
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={sendForm.persistent}
                      onChange={(e) => handleSendFormChange('persistent', e.target.checked)}
                    />
                    Persistent
                  </label>
                </div>
              </div>
              <div className="form-group full-width">
                <label>Content *</label>
                <textarea
                  className="form-control content-textarea"
                  value={sendForm.content}
                  onChange={(e) => handleSendFormChange('content', e.target.value)}
                  placeholder="Enter notification content"
                  rows={4}
                />
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSendNotification}
                  disabled={sending || !sendForm.user_id.trim() || !sendForm.subject.trim() || !sendForm.content.trim()}
                >
                  {sending ? <LoadingSpinner /> : 'Send Notification'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {deleteAllowed() && (
        <div className="danger-zone">
          <h5>Danger Zone</h5>
          <p>Once you delete this notification, there is no going back. Please be certain.</p>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleDeleteNotification}
          >
            Delete Notification
          </button>
        </div>
      )}

      {error && (
        <div className={`alert ${error.includes('successfully') ? 'alert-success' : 'alert-danger'}`}>
          {!error.includes('successfully') && <RedTriangleIcon />}
          <strong>{error.includes('successfully') ? 'Success:' : 'Error:'}</strong> {error}
        </div>
      )}

      <ConfirmDialog
        isOpen={dialogState.isOpen}
        title={dialogState.title}
        message={dialogState.message}
        onConfirm={handleConfirm}
        onCancel={closeConfirmDialog}
      />
    </div>
  );
};

export default NotificationDetail;
